import React from 'react';
import './AvatarInput.scss';
import { ReactComponent as CameraIcon } from 'assets/icons/mingcute_camera-2-fill.svg';

const WIDTH = 640;
const HEIGHT = 768;
const SQUARE = 160;

interface Offset {
    x: number;
    y: number;
}

interface DragStart extends Offset {
    clientX: number;
    clientY: number;
}

export interface AvatarInputProps {
    file: Blob | undefined;
    setFile: (value: Blob) => void;
    existingUrl?: string;
}

function boundedOffset(
    image: HTMLImageElement,
    zoom: number,
    x: number,
    y: number
): Offset {
    const scale =
        Math.max(WIDTH / image.naturalWidth, HEIGHT / image.naturalHeight) *
        zoom;
    const maxX = (image.naturalWidth * scale - WIDTH) / 2;
    const maxY = (image.naturalHeight * scale - HEIGHT) / 2;
    return {
        x: Math.max(-maxX, Math.min(maxX, x)),
        y: Math.max(-maxY, Math.min(maxY, y)),
    };
}

function drawCrop(
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    zoom: number,
    offset: Offset
): void {
    const context = canvas.getContext('2d');
    if (!context) return;
    const scale =
        Math.max(WIDTH / image.naturalWidth, HEIGHT / image.naturalHeight) *
        zoom;
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, WIDTH, HEIGHT);
    context.drawImage(
        image,
        (WIDTH - width) / 2 + offset.x,
        (HEIGHT - height) / 2 + offset.y,
        width,
        height
    );
}

function AvatarInput({
    file,
    setFile,
    existingUrl,
}: AvatarInputProps): React.ReactElement {
    const [source, setSource] = React.useState<File | null>(null);
    const [image, setImage] = React.useState<HTMLImageElement | null>(null);
    const [pictureUrl, setPictureUrl] = React.useState('');
    const [zoom, setZoom] = React.useState(1);
    const [offset, setOffset] = React.useState<Offset>({ x: 0, y: 0 });
    const [error, setError] = React.useState('');
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const squareRef = React.useRef<HTMLCanvasElement>(null);
    const dragRef = React.useRef<DragStart | null>(null);

    React.useEffect(() => {
        if (!file) {
            setPictureUrl('');
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setPictureUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    React.useEffect(() => {
        if (!source) {
            setImage(null);
            return undefined;
        }
        let active = true;
        const url = URL.createObjectURL(source);
        const loaded = new Image();
        loaded.onload = () => {
            if (!active) return;
            setImage(loaded);
            setZoom(1);
            setOffset({ x: 0, y: 0 });
            setError('');
        };
        loaded.onerror = () => {
            if (active) setError('Не удалось открыть фото. Выбери другое.');
        };
        loaded.src = url;
        return () => {
            active = false;
            URL.revokeObjectURL(url);
        };
    }, [source]);

    React.useEffect(() => {
        if (canvasRef.current && image) {
            drawCrop(canvasRef.current, image, zoom, offset);
            const context = squareRef.current?.getContext('2d');
            context?.drawImage(
                canvasRef.current,
                0,
                (HEIGHT - WIDTH) / 2,
                WIDTH,
                WIDTH,
                0,
                0,
                SQUARE,
                SQUARE
            );
        }
    }, [image, zoom, offset]);

    const onPointerDown = (
        event: React.PointerEvent<HTMLCanvasElement>
    ): void => {
        if (!image) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
            clientX: event.clientX,
            clientY: event.clientY,
            x: offset.x,
            y: offset.y,
        };
    };

    const onPointerMove = (
        event: React.PointerEvent<HTMLCanvasElement>
    ): void => {
        if (!image || !dragRef.current) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const x =
            dragRef.current.x +
            ((event.clientX - dragRef.current.clientX) * WIDTH) / rect.width;
        const y =
            dragRef.current.y +
            ((event.clientY - dragRef.current.clientY) * HEIGHT) / rect.height;
        setOffset(boundedOffset(image, zoom, x, y));
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLCanvasElement>): void => {
        if (!image) return;
        const movement: Record<string, Offset> = {
            ArrowLeft: { x: -12, y: 0 },
            ArrowRight: { x: 12, y: 0 },
            ArrowUp: { x: 0, y: -12 },
            ArrowDown: { x: 0, y: 12 },
        };
        const step = movement[event.key];
        if (!step) return;
        event.preventDefault();
        setOffset(state =>
            boundedOffset(image, zoom, state.x + step.x, state.y + step.y)
        );
    };

    const save = (): void => {
        if (!canvasRef.current || !image) return;
        const output = document.createElement('canvas');
        const scale = Math.min(
            1,
            image.naturalWidth / WIDTH,
            image.naturalHeight / HEIGHT
        );
        output.width = Math.max(1, Math.round(WIDTH * scale));
        output.height = Math.max(1, Math.round(HEIGHT * scale));
        output
            .getContext('2d')
            ?.drawImage(canvasRef.current, 0, 0, output.width, output.height);
        output.toBlob(
            blob => {
                if (!blob) {
                    setError('Не удалось сохранить фото. Попробуй ещё раз.');
                    return;
                }
                setFile(
                    new File([blob], 'candidate-photo.jpg', {
                        type: 'image/jpeg',
                    })
                );
                setSource(null);
            },
            'image/jpeg',
            0.82
        );
    };

    return (
        <div className="rd-photo-input">
            <label
                className="rd-photo-input__upload"
                htmlFor="candidate-photo-upload"
            >
                <input
                    id="candidate-photo-upload"
                    accept="image/*"
                    type="file"
                    onChange={event => {
                        const input = event.currentTarget;
                        const selected = input.files?.[0];
                        if (selected) setSource(selected);
                        input.value = '';
                    }}
                />
                <span className="rd-photo-input__thumb">
                    {pictureUrl || existingUrl ? (
                        <img src={pictureUrl || existingUrl} alt="" />
                    ) : (
                        <CameraIcon />
                    )}
                </span>
                <span>
                    <strong>
                        {file || existingUrl
                            ? 'Изменить фотографию'
                            : 'Загрузить фотографию'}
                    </strong>
                    <small>
                        Подгони фото для вертикальной и квадратной карточек
                    </small>
                </span>
            </label>

            {source && (
                <div className="rd-photo-input__editor">
                    <canvas
                        ref={canvasRef}
                        width={WIDTH}
                        height={HEIGHT}
                        aria-label="Кадрирование фотографии"
                        tabIndex={0}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={() => {
                            dragRef.current = null;
                        }}
                        onPointerCancel={() => {
                            dragRef.current = null;
                        }}
                        onKeyDown={onKeyDown}
                    />
                    <div className="rd-photo-input__controls">
                        <h3>Кадрирование</h3>
                        <p>
                            Перетащи фото так, чтобы лицо было видно и в
                            вертикальном, и в квадратном кадре.
                        </p>
                        <div className="rd-photo-input__square-preview">
                            <canvas
                                ref={squareRef}
                                width={SQUARE}
                                height={SQUARE}
                                aria-label="Квадратный вид фотографии"
                            />
                            <span>Квадратная карточка</span>
                        </div>
                        <label htmlFor="photo-zoom">
                            Масштаб
                            <input
                                id="photo-zoom"
                                type="range"
                                min="1"
                                max="3"
                                step="0.01"
                                value={zoom}
                                onChange={event => {
                                    const value = Number(event.target.value);
                                    setZoom(value);
                                    if (image)
                                        setOffset(state =>
                                            boundedOffset(
                                                image,
                                                value,
                                                state.x,
                                                state.y
                                            )
                                        );
                                }}
                            />
                        </label>
                        {error && <p role="alert">{error}</p>}
                        <div className="rd-photo-input__actions">
                            <button
                                type="button"
                                className="rd-btn rd-btn--plain"
                                onClick={() => setSource(null)}
                            >
                                Отмена
                            </button>
                            <button
                                type="button"
                                className="rd-btn rd-btn--green"
                                onClick={save}
                                disabled={!image}
                            >
                                Использовать фото
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

AvatarInput.defaultProps = { existingUrl: '' };

export default React.memo(AvatarInput);
